import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, Zap, CalendarClock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PriorityAssessment = () => {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    axios.get(`${API}/payments/config`).then((r) => setCfg(r.data)).catch(() => setCfg({ enabled: false }));
  }, []);

  const pay = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/payments/assessment/order`, { ...form, gateway: "" });
      if (data.redirect_url) {
        // EaseBuzz hosted checkout: server signed + initiated, we just redirect
        window.location.assign(data.redirect_url);
        return;
      }
      // PayU hosted checkout: POST the signed fields straight to PayU
      const f = document.createElement("form");
      f.method = "POST";
      f.action = data.action;
      Object.entries(data.fields).forEach(([name, value]) => {
        const i = document.createElement("input");
        i.type = "hidden";
        i.name = name;
        i.value = value ?? "";
        f.appendChild(i);
      });
      document.body.appendChild(f);
      f.submit();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start the payment — please try again.");
      setBusy(false);
    }
  };

  const input = "w-full bg-white/[0.04] border border-white/15 text-slate-100 placeholder:text-slate-500 px-4 py-3 text-sm focus:outline-none focus:border-nx-goldlight/70 transition-colors";

  if (!cfg?.enabled) return null;

  return (
    <div className="border border-nx-gold/30 bg-nx-gold/[0.04] p-8" data-testid="priority-assessment">
      <div className="flex items-center justify-between mb-5">
        <p className="text-[10px] tracking-[0.28em] uppercase text-nx-goldlight flex items-center gap-2"><Zap size={13} /> Priority Booking</p>
        <span className="font-marcellus text-2xl text-white" data-testid="priority-fee">₹{cfg.assessment_fee_inr?.toLocaleString("en-IN")}</span>
      </div>
      <p className="text-sm text-slate-300 leading-relaxed mb-6">
        Skip the queue — a paid priority assessment confirms an engineer visit within <span className="text-white">2 business days</span>, with the fee adjusted against your final project quote.
      </p>
      <div className="flex flex-col gap-2.5 mb-6 text-xs text-slate-400">
        <span className="flex items-center gap-2.5"><CalendarClock size={13} className="text-nx-goldlight shrink-0" /> Confirmed slot within 2 business days</span>
        <span className="flex items-center gap-2.5"><ShieldCheck size={13} className="text-nx-goldlight shrink-0" /> Fee adjusted in your final project quote</span>
      </div>
      <form onSubmit={pay} className="flex flex-col gap-4" data-testid="priority-assessment-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <input required placeholder="Full name" className={input} value={form.name} onChange={set("name")} data-testid="priority-name-input" />
          <input required type="tel" placeholder="Phone" className={input} value={form.phone} onChange={set("phone")} data-testid="priority-phone-input" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <input required type="email" placeholder="Email" className={input} value={form.email} onChange={set("email")} data-testid="priority-email-input" />
          <input required placeholder="Site city / district" className={input} value={form.city} onChange={set("city")} data-testid="priority-city-input" />
        </div>
        <textarea rows={2} placeholder="Anything we should know? (optional)" className={`${input} resize-none`} value={form.notes} onChange={set("notes")} data-testid="priority-notes-input" />
        <button type="submit" disabled={busy} className="bg-nx-gold hover:bg-nx-goldlight text-nx-navy font-semibold tracking-wide text-sm px-8 py-4 transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-60" data-testid="priority-pay-button">
          {busy ? "Opening secure payment…" : `Pay ₹${cfg.assessment_fee_inr?.toLocaleString("en-IN")} & Book Priority Visit`}
        </button>
        <p className="text-[11px] text-slate-600 leading-relaxed">Secure payment by PayU — UPI, cards and netbanking accepted. Instant confirmation by email.</p>
      </form>
    </div>
  );
};
